/**
 * LA ICE JEWELS — Centralized Form Submission Handler
 *
 * Connects all customer enquiry forms directly to the Cloudflare Worker API:
 * https://laice-forms-api.laice-forms.workers.dev/api/submit-enquiry
 *
 * Supported Forms:
 * 1. Custom Jewellery Design Request (#laice-cdr-form on page.customedesign)
 * 2. Contact Us Bespoke Form (#laice-custom-contact-form on page.contact-2)
 * 3. Custom Design Inquiry block (ai_gen_block_e07f96f)
 * 4. Send Us An Email / Contact Form block (ai_gen_block_1ec96d0 / page.cform)
 * 5. Ask an Expert Product Drawer (blocks/_ask-an-expert.liquid)
 * 6. Contact Form blocks (blocks/contact-form.liquid, ai_gen_block_2dacfc2)
 *
 * Safety Constraints:
 * - Newsletters, customer accounts, cart, checkout, and back-in-stock forms are strictly ignored.
 * - Existing designs, CSS, layouts, fields, and classes are 100% preserved.
 * - Handles reference photo uploads, loading states, validation, and user feedback.
 */
(function () {
  'use strict';

  var DEFAULT_ENDPOINT = 'https://laice-forms-api.laice-forms.workers.dev/api/submit-enquiry';
  var API_ENDPOINT = (window.LAICE_CONFIG && window.LAICE_CONFIG.apiEndpoint) || window.LAICE_API_ENDPOINT || DEFAULT_ENDPOINT;

  var PAGE_LOAD_TIME = Date.now();

  function isIgnoredForm(form) {
    if (!form) return true;

    // Check newsletter tags
    var tagsInput = form.querySelector('input[name="contact[tags]"], input[name="tags"]');
    if (tagsInput && tagsInput.value && tagsInput.value.toLowerCase().indexOf('newsletter') !== -1) {
      return true;
    }

    // Check newsletter class/container
    if (
      form.closest('.newsletter') ||
      form.closest('.newsletter-form') ||
      form.closest('#newsletter') ||
      form.closest('[id*="NewsletterForm"]') ||
      form.closest('[class*="newsletter"]') ||
      (form.id && form.id.toLowerCase().indexOf('newsletter') !== -1)
    ) {
      return true;
    }

    // Check cart and checkout
    var action = (form.getAttribute('action') || '').toLowerCase();
    if (action.indexOf('/cart') !== -1 || action.indexOf('/checkout') !== -1) {
      return true;
    }

    // Check customer account login/register
    if (action.indexOf('/account') !== -1 || (form.id && form.id.indexOf('customer') !== -1)) {
      return true;
    }

    // Check back-in-stock alerts
    if (
      form.dataset.backInStock === 'true' ||
      form.closest('back-in-stock') ||
      form.closest('#BackInStockForm') ||
      (form.id && form.id.indexOf('BackInStock') !== -1) ||
      (form.className && form.className.indexOf('back-instock') !== -1)
    ) {
      return true;
    }

    return false;
  }

  function resolveFormName(form) {
    if (form.dataset.formName) {
      return form.dataset.formName;
    }
    if (form.id === 'laice-cdr-form') {
      return 'Custom Jewellery Design Request';
    }
    if (form.id === 'laice-custom-contact-form') {
      return 'Contact Us';
    }

    // Check for subject field
    var subjectInput = form.querySelector('input[name="contact[subject]"], input[name="subject"]');
    if (subjectInput && subjectInput.value && subjectInput.value.trim() !== '') {
      return subjectInput.value.trim();
    }

    // Check parent drawer / block attributes
    var drawer = form.closest('contact-form, [data-contact-form-type]');
    if (drawer && drawer.getAttribute('data-contact-form-type') === 'ask-an-expert') {
      return 'Ask an Expert';
    }

    // Look for heading in block
    var container = form.closest('section, [class*="contact"], [class*="la-ice-custom-form"], [class*="ai-contact"]');
    if (container) {
      var heading = container.querySelector('h1, h2, h3, .heading');
      if (heading && heading.textContent && heading.textContent.trim() !== '') {
        return heading.textContent.trim();
      }
    }

    return document.title ? document.title.split('–')[0].split('-')[0].trim() : 'Customer Enquiry';
  }

  function findStatusElement(form) {
    if (!form) return null;

    // 1. Check if we already created a dedicated status message element for this form
    var statusEl = form.querySelector('.laice-form-status-msg');
    if (statusEl) return statusEl;

    // 2. Custom Jewellery Design Request (#laice-cdr-form)
    if (form.id === 'laice-cdr-form' || form.querySelector('#laice-cdr-note')) {
      statusEl = form.querySelector('#laice-cdr-note');
      if (statusEl) return statusEl;
    }

    // 3. Contact Us Bespoke Form (#laice-custom-contact-form)
    if (form.id === 'laice-custom-contact-form') {
      var wrapper = form.closest('.laice-contact-form-wrapper') || form.parentElement;
      if (wrapper) {
        statusEl = wrapper.querySelector('#laice-contact-status') || wrapper.querySelector('.laice-contact-status');
        if (statusEl) return statusEl;
      }
    }

    // 4. Check for existing dedicated status container in surrounding block wrapper
    var container = form.closest('.laice-contact-form-wrapper, .la-ice-custom-form__form-wrapper, .ai-contact-form-wrapper, .ai-contact-form__container');
    if (container) {
      statusEl = container.querySelector('.laice-form-status-msg, .laice-contact-status');
      if (statusEl) return statusEl;
    }

    // 5. Create a dedicated status message element inside the form
    statusEl = document.createElement('div');
    statusEl.className = 'laice-form-status-msg';
    statusEl.style.boxSizing = 'border-box';
    statusEl.style.marginTop = '18px';
    statusEl.style.marginBottom = '12px';
    statusEl.style.padding = '14px 18px';
    statusEl.style.borderRadius = '5px';
    statusEl.style.fontSize = '14px';
    statusEl.style.lineHeight = '1.5';
    statusEl.style.textAlign = 'center';
    statusEl.style.display = 'none';
    statusEl.setAttribute('role', 'status');
    statusEl.setAttribute('aria-live', 'polite');

    var submitBtn = form.querySelector('button[type="submit"]') || form.querySelector('input[type="submit"]');
    if (submitBtn && submitBtn.parentNode && submitBtn.parentNode !== form) {
      submitBtn.parentNode.parentNode.insertBefore(statusEl, submitBtn.parentNode.nextSibling);
    } else if (submitBtn && submitBtn.parentNode === form) {
      form.insertBefore(statusEl, submitBtn.nextSibling);
    } else {
      form.appendChild(statusEl);
    }

    return statusEl;
  }

  function showStatus(el, message, isSuccess) {
    if (!el) return;

    if (!message) {
      el.style.display = 'none';
      el.textContent = '';
      return;
    }

    el.textContent = message;
    el.style.setProperty('display', 'block', 'important');
    el.style.setProperty('box-sizing', 'border-box', 'important');
    el.style.setProperty('padding', '14px 18px', 'important');
    el.style.setProperty('border-radius', '5px', 'important');
    el.style.setProperty('margin-top', '16px', 'important');
    el.style.setProperty('font-size', '14px', 'important');
    el.style.setProperty('line-height', '1.5', 'important');
    el.style.setProperty('text-align', 'center', 'important');

    el.classList.remove('is-success', 'is-error', 'is-visible');
    el.classList.add('is-visible');

    if (isSuccess === true) {
      el.classList.add('is-success');
      el.style.setProperty('background-color', '#F3FAF4', 'important');
      el.style.setProperty('color', '#245C35', 'important');
      el.style.setProperty('border', '1px solid #B8D8C0', 'important');
    } else if (isSuccess === false) {
      el.classList.add('is-error');
      el.style.setProperty('background-color', '#FFF5F5', 'important');
      el.style.setProperty('color', '#8B1E2D', 'important');
      el.style.setProperty('border', '1px solid #E2B8B8', 'important');
    }

    try {
      if (typeof el.scrollIntoView === 'function') {
        el.scrollIntoView({ behavior: 'smooth', block: 'nearest' });
      }
    } catch (e) {
      // Ignore scroll errors in sandboxed iframes
    }
  }

  function initForm(form) {
    if (!form || form.dataset.laiceInit === 'true') return;
    if (isIgnoredForm(form)) return;

    form.dataset.laiceInit = 'true';

    // 1. Ensure honeypot field exists for spam protection
    var hp = form.querySelector('input[name="_laice_hp"]');
    if (!hp) {
      hp = document.createElement('input');
      hp.type = 'text';
      hp.name = '_laice_hp';
      hp.style.position = 'absolute';
      hp.style.opacity = '0';
      hp.style.height = '0';
      hp.style.width = '0';
      hp.style.zIndex = '-1';
      hp.tabIndex = -1;
      hp.autocomplete = 'off';
      form.appendChild(hp);
    }

    // 2. Ensure file input has name attribute and store default label text
    var fileInput = form.querySelector('input[type="file"]');
    if (fileInput) {
      if (!fileInput.name) {
        fileInput.name = 'Reference Photos';
      }
      var fileText = form.querySelector('[id^="file-text-"]') || form.querySelector('.laice-cdr-file-note');
      if (fileText && !fileText.dataset.defaultText) {
        fileText.dataset.defaultText = fileText.textContent.trim();
      }
    }

    // 3. Intercept submission (Capture phase guarantees pre-emption over inline mailto/placeholders)
    form.addEventListener('submit', function (event) {
      event.preventDefault();
      event.stopPropagation();
      if (typeof event.stopImmediatePropagation === 'function') {
        event.stopImmediatePropagation();
      }

      // Prevent duplicate submissions while in flight
      if (form.dataset.laiceSubmitting === 'true') {
        return;
      }

      // HTML5 validation
      if (typeof form.reportValidity === 'function' && !form.reportValidity()) {
        return;
      }

      var statusEl = findStatusElement(form);

      // Validate files if present
      if (fileInput && fileInput.files && fileInput.files.length > 0) {
        if (fileInput.files.length > 5) {
          showStatus(statusEl, 'You can upload a maximum of 5 reference photos.', false);
          return;
        }
        for (var i = 0; i < fileInput.files.length; i++) {
          var f = fileInput.files[i];
          if (f.size > 5 * 1024 * 1024) {
            showStatus(statusEl, 'File "' + f.name + '" exceeds the 5MB size limit. Please choose a smaller photo.', false);
            return;
          }
        }
      }

      var submitBtn = form.querySelector('button[type="submit"]') || form.querySelector('input[type="submit"]');
      var origBtnHtml = submitBtn ? submitBtn.innerHTML : '';

      // Lock form against duplicate submits & display loading state
      form.dataset.laiceSubmitting = 'true';
      if (submitBtn) {
        submitBtn.disabled = true;
        submitBtn.setAttribute('aria-busy', 'true');
        var loadingText = submitBtn.dataset.loadingText || 'Submitting...';
        submitBtn.textContent = loadingText;
      }

      showStatus(statusEl, '', null);

      // Build submission payload
      var formData = new FormData(form);

      // Add anti-spam timing & page URL metadata
      formData.set('_laice_ts', PAGE_LOAD_TIME);
      if (!formData.get('page_url')) {
        formData.set('page_url', window.location.href);
      }

      var formName = resolveFormName(form);
      if (!formData.get('form_name')) {
        formData.set('form_name', formName);
      }

      // Submit via Cloudflare Workers API
      fetch(API_ENDPOINT, {
        method: 'POST',
        body: formData,
        mode: 'cors'
      })
      .then(function (response) {
        return response.json()
          .then(function (data) {
            return { ok: response.ok, status: response.status, data: data };
          })
          .catch(function () {
            return { ok: response.ok, status: response.status, data: {} };
          });
      })
      .then(function (res) {
        if (res.ok && res.data && res.data.success) {
          var successMsg = res.data.message || 'Thank you! Your enquiry has been received. We will get back to you within 1 to 2 business days.';
          showStatus(statusEl, successMsg, true);

          // Wrap all reset and DOM cleanup in try/catch so an error can never reject into .catch
          try {
            if (typeof form.reset === 'function') {
              form.reset();
            }
          } catch (resetErr) {
            console.warn('[LA ICE FORMS] form.reset error:', resetErr);
          }

          // Reset file label text if custom label exists
          try {
            var fileLabel = form.querySelector('[id^="file-text-"]') || form.querySelector('.laice-cdr-file-note');
            if (fileLabel && fileLabel.dataset.defaultText) {
              fileLabel.textContent = fileLabel.dataset.defaultText;
            }
          } catch (e) {}

          // Reset category selector buttons to default first item
          try {
            var catBtns = form.querySelectorAll('.laice-cdr-category, [data-category]');
            if (catBtns.length > 0) {
              catBtns.forEach(function (b) { b.classList.remove('active'); });
              catBtns[0].classList.add('active');
            }
          } catch (e) {}

        } else {
          var errorMsg = (res.data && res.data.error) || 'We were unable to submit your enquiry at this time. Please try again or reach out directly to laicelondon@gmail.com.';
          showStatus(statusEl, errorMsg, false);
        }
      })
      .catch(function (err) {
        console.error('[LA ICE FORMS API ERROR]', err);
        showStatus(statusEl, 'A network error occurred. Please check your connection or email us directly at laicelondon@gmail.com.', false);
      })
      .finally(function () {
        form.dataset.laiceSubmitting = 'false';
        if (submitBtn) {
          submitBtn.disabled = false;
          submitBtn.removeAttribute('aria-busy');
          submitBtn.innerHTML = origBtnHtml;
        }
      });
    }, true);
  }

  function scanForms() {
    var selectors = [
      '#laice-cdr-form',
      '#laice-custom-contact-form',
      'contact-form[data-contact-form-type="ask-an-expert"] form',
      'contact-form.block-contact-form form',
      '[class*="la-ice-custom-form"] form',
      '[class*="ai-contact-form"] form',
      '[class*="ai-contact-section"] form',
      'form[data-laice-enquiry-form="true"]'
    ];

    selectors.forEach(function (sel) {
      var matches = document.querySelectorAll(sel);
      matches.forEach(initForm);
    });
  }

  // Initial scan on load
  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', scanForms);
  } else {
    scanForms();
  }

  // Observe dynamically loaded drawers (e.g. Ask an Expert popup)
  if (typeof MutationObserver !== 'undefined') {
    var observer = new MutationObserver(function () {
      scanForms();
    });
    observer.observe(document.body, { childList: true, subtree: true });
  }

  window.LaIceForms = {
    init: initForm,
    scan: scanForms,
    getEndpoint: function () { return API_ENDPOINT; }
  };
})();
